import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "react-qr-code";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowRightLeft } from "lucide-react";

const inputCls = "mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E2240]";
const statusCls = {
  requested: "bg-amber-100 text-amber-700", approved: "bg-blue-100 text-blue-700",
  in_transit: "bg-indigo-100 text-indigo-700", active: "bg-emerald-100 text-emerald-700",
  returning: "bg-amber-100 text-amber-700", return_in_transit: "bg-indigo-100 text-indigo-700",
  returned: "bg-slate-100 text-slate-600", completed: "bg-slate-100 text-slate-600", rejected: "bg-red-100 text-red-700",
};

export default function Transfers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [show, setShow] = useState(false);
  const [qr, setQr] = useState(null);
  const [f, setF] = useState({ vehicle_id: "", type: "temporary", to_branch_id: "", start_date: "", planned_return_date: "", reason: "", driver_name: "", driver_kec_id: "" });
  const nav = useNavigate();
  const canRequest = ["sm", "vp", "admin"].includes(user.role);
  const canApprove = ["zh", "vp", "admin"].includes(user.role);
  const canOperate = ["tdc", "sm", "admin"].includes(user.role);

  const load = () => {
    api.get("/transfers").then((r) => setItems(r.data));
    api.get("/vehicles").then((r) => setVehicles(r.data));
    api.get("/branches").then((r) => setBranches(r.data));
    api.get("/kec-roster").then((r) => setKecs(r.data.filter((k) => k.active))).catch(() => {});
  };
  useEffect(load, []);

  const submit = async () => {
    try {
      const { data } = await api.post("/transfers", { ...f, to_branch_id: f.to_branch_id || undefined, planned_return_date: f.planned_return_date || null, driver_kec_id: f.driver_kec_id || null, driver_name: f.driver_name || null });
      toast.success(data.status === "approved" ? "Transfer approved (VP-initiated) — gate pass issued" : "Transfer requested — sent for approval");
      setShow(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const act = async (id, action, params = "") => {
    try {
      await api.post(`/transfers/${id}/${action}${params}`);
      toast.success(`Transfer ${action.replace("-", " ")} done`); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const arrival = async (t) => {
    const odo = prompt(`Arrival odometer for ${t.reg_no} (km)?`);
    if (!odo) return;
    try { await api.post(`/transfers/${t.id}/confirm-arrival`, { odometer: parseInt(odo), photos: [] }); toast.success("Arrival confirmed — car live on destination board"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const doReturn = async (t) => {
    const odo = prompt(`Return odometer for ${t.reg_no} (km)? (Admin manual correction — normally the home gate-in completes the return)`);
    if (!odo) return;
    act(t.id, "return", `?odometer=${parseInt(odo)}`);
  };
  const [ret, setRet] = useState(null);
  const [retDriver, setRetDriver] = useState({ kec_roster_id: "", driver_name: "" });
  const [kecs, setKecs] = useState([]);
  const openReturn = (t) => { setRet(t); setRetDriver({ kec_roster_id: "", driver_name: "" }); };
  const addDrive = async (t) => {
    try { const { data } = await api.post(`/transfers/${t.id}/drive-trip`); nav(`/trip/${data.trip_id}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const startReturn = async () => {
    try {
      const { data } = await api.post(`/transfers/${ret.id}/start-return`, { kec_roster_id: retDriver.kec_roster_id || null, driver_name: retDriver.driver_name || null });
      toast.success(data.status === "returning" ? "Return gate pass issued — show the QR at your gate" : "Return started"); setRet(null); load(); setQr(ret.id);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="px-4 py-5 lg:px-8 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#0E2240]">Inter-Branch Transfers</h1>
          <p className="text-xs text-slate-500 mt-0.5">Both locations under one Zonal Head → ZH approves · otherwise → VP approves</p>
        </div>
        {canRequest && <button data-testid="transfer-request-btn" onClick={() => setShow(!show)}
          className="shrink-0 whitespace-nowrap rounded-full bg-[#BB162B] px-4 py-2 text-sm font-bold text-white">+ Request</button>}
      </div>

      {show && (
        <div className="mt-4 space-y-3 rounded-2xl border border-slate-200 bg-white p-4">
          <select data-testid="transfer-vehicle-select" value={f.vehicle_id} onChange={(e) => setF({ ...f, vehicle_id: e.target.value })} className={inputCls}>
            <option value="">Select vehicle (any branch)…</option>
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.reg_no} — {v.model} ({branches.find((b) => b.id === (v.current_branch_id || v.branch_id))?.name || v.branch_id})</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <select data-testid="transfer-type-select" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className={inputCls}>
              <option value="temporary">Temporary</option><option value="permanent">Permanent</option>
            </select>
            <select data-testid="transfer-to-branch" value={f.to_branch_id} onChange={(e) => setF({ ...f, to_branch_id: e.target.value })} className={inputCls}>
              <option value="">{user.role === "sm" ? `To: my branch` : "To branch…"}</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <div><label className="text-[10px] uppercase text-slate-400">Start date</label>
              <input data-testid="transfer-start-date" type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className={inputCls} /></div>
            {f.type === "temporary" && <div><label className="text-[10px] uppercase text-slate-400">Planned return</label>
              <input data-testid="transfer-return-date" type="date" value={f.planned_return_date} onChange={(e) => setF({ ...f, planned_return_date: e.target.value })} className={inputCls} /></div>}
            <select data-testid="transfer-driver-kec-select" value={f.driver_kec_id} onChange={(e) => setF({ ...f, driver_kec_id: e.target.value, driver_name: "" })} className={`${inputCls} col-span-2`}>
              <option value="">Outside driver (type the name below)</option>
              {kecs.map((k) => <option key={k.id} value={k.id}>KEC · {k.kec_name}</option>)}
            </select>
            {!f.driver_kec_id && <input data-testid="transfer-driver-input" value={f.driver_name} onChange={(e) => setF({ ...f, driver_name: e.target.value })} placeholder="Outside driver name" className={`${inputCls} col-span-2`} />}
            <input data-testid="transfer-reason-input" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="Reason (e.g. EV9 demand, no unit here)" className={`${inputCls} col-span-2`} />
          </div>
          <p className="text-[10px] text-slate-500">Pick a KEC as driver if test drives may be given on the way — only a named KEC can add drives to the transfer.</p>
          <button data-testid="transfer-submit-btn" onClick={submit} className="w-full rounded-xl bg-[#0E2240] py-3 text-sm font-bold text-white">Submit Transfer Request</button>
        </div>
      )}

      <div className="mt-4 space-y-3">
        {items.length === 0 && <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">No transfers</div>}
        {items.map((t) => (
          <div key={t.id} data-testid={`transfer-card-${t.reg_no}`} className={`rounded-xl border bg-white p-4 shadow-sm ${t.overdue ? "border-red-300" : "border-slate-200"}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-heading font-bold text-[#0E2240] flex items-center gap-2">{t.reg_no} · {t.model} <ArrowRightLeft size={13} className="text-slate-400" /></div>
                <div className="text-xs text-slate-500 mt-0.5">{t.from_branch_name} → {t.to_branch_name} · {t.type} · {t.scope.replace("_", "-")}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">{t.start_date}{t.planned_return_date && ` → due back ${t.planned_return_date}`} · driver {t.driver_name}{t.driver_kec_id && <span className="ml-1 rounded-full bg-indigo-100 px-1.5 text-[9px] font-bold text-indigo-700">KEC</span>}</div>
                {(t.drives_given > 0 || t.drive_trip_id) && <div data-testid={`transfer-drives-${t.reg_no}`} className="mt-1 inline-block rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-bold text-purple-800">Drives on transfer · {t.drives_given} given</div>}
                <div className="text-[11px] text-slate-400">{t.reason}</div>
                {t.overdue && <div className="mt-1 text-[11px] font-bold text-[#BB162B]">⚠ Return overdue</div>}
              </div>
              <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${statusCls[t.status]}`}>{t.status.replace(/_/g, " ")}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {t.status === "requested" && canApprove && (
                <>
                  <button data-testid={`transfer-approve-${t.reg_no}`} onClick={() => act(t.id, "approve")} className="rounded-xl bg-emerald-600 px-4 py-2 text-xs font-bold text-white">Approve</button>
                  <button onClick={() => act(t.id, "reject")} className="rounded-xl border border-red-300 px-4 py-2 text-xs font-bold text-[#BB162B]">Reject</button>
                </>
              )}
              {["approved", "in_transit"].includes(t.status) && t.qr_token && (
                <button data-testid={`transfer-qr-${t.reg_no}`} onClick={() => setQr(qr === t.id ? null : t.id)} className="rounded-xl border border-slate-300 px-4 py-2 text-xs font-bold text-slate-600">
                  {qr === t.id ? "Hide" : "Show"} Transfer Gate Pass
                </button>
              )}
              {["approved", "in_transit"].includes(t.status) && canOperate && user.branch_id === t.to_branch_id && (
                <button data-testid={`transfer-arrival-${t.reg_no}`} onClick={() => arrival(t)} className="rounded-xl bg-[#0E2240] px-4 py-2 text-xs font-bold text-white">Confirm Arrival</button>
              )}
              {((t.status === "in_transit" && t.driver_kec_id) || (t.status === "return_in_transit" && t.return_kec_roster_id)) && (user.role === "admin" || user.role === "sm" || user.id === (t.status === "in_transit" ? t.driver_kec_id : t.return_kec_roster_id)) && (
                <button data-testid={`transfer-add-drive-${t.reg_no}`} onClick={() => addDrive(t)} className="rounded-xl bg-purple-700 px-4 py-2 text-xs font-bold text-white">{t.drive_trip_id ? "Drives on this transfer" : "Add a test drive"}</button>
              )}
              {t.drive_trip_id && !["in_transit", "return_in_transit"].includes(t.status) && (
                <button data-testid={`transfer-view-drives-${t.reg_no}`} onClick={() => nav(`/trip/${t.drive_trip_id}`)} className="rounded-xl border border-purple-300 px-4 py-2 text-xs font-bold text-purple-800">View drives given</button>
              )}
              {t.status === "active" && t.type === "temporary" && canOperate && (user.role === "admin" || [t.to_branch_id, t.from_branch_id].includes(user.branch_id)) && (
                <button data-testid={`transfer-start-return-${t.reg_no}`} onClick={() => openReturn(t)} className="rounded-xl bg-[#0E2240] px-4 py-2 text-xs font-bold text-white">Return to {t.from_branch_name}</button>
              )}
              {["returning", "return_in_transit"].includes(t.status) && t.return_qr_token && (
                <button data-testid={`transfer-return-qr-${t.reg_no}`} onClick={() => setQr(qr === t.id ? null : t.id)} className="rounded-xl border border-slate-300 px-4 py-2 text-xs font-bold text-slate-600">
                  {qr === t.id ? "Hide" : "Show"} Return Gate Pass
                </button>
              )}
              {t.status === "returning" && canOperate && (
                <button data-testid={`transfer-cancel-return-${t.reg_no}`} onClick={() => act(t.id, "cancel-return")} className="rounded-xl border border-red-300 px-4 py-2 text-xs font-bold text-[#BB162B]">Cancel return</button>
              )}
              {["active", "returning", "return_in_transit"].includes(t.status) && user.role === "admin" && (
                <button data-testid={`transfer-return-${t.reg_no}`} onClick={() => doReturn(t)} className="rounded-xl border border-slate-300 px-4 py-2 text-xs font-bold text-slate-600">Mark Returned (manual)</button>
              )}
            </div>
            {["returning", "return_in_transit"].includes(t.status) && (
              <div className="mt-2 text-[11px] text-slate-500">Return driver: <b>{t.return_driver_name}</b> · {t.status === "returning" ? `awaiting gate-OUT at ${t.to_branch_name}` : `on the road — gate-IN at ${t.from_branch_name} completes the return`}</div>
            )}
            {ret?.id === t.id && (
              <div data-testid="return-driver-form" className="mt-3 space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="text-xs font-bold text-[#0E2240]">Who is driving {t.reg_no} back to {t.from_branch_name}?</div>
                <select data-testid="return-kec-select" value={retDriver.kec_roster_id} onChange={(e) => setRetDriver({ kec_roster_id: e.target.value, driver_name: "" })} className={inputCls}>
                  <option value="">Outside driver (type the name below)</option>
                  {kecs.map((k) => <option key={k.id} value={k.id}>KEC · {k.kec_name}</option>)}
                </select>
                {!retDriver.kec_roster_id && <input data-testid="return-driver-input" value={retDriver.driver_name} onChange={(e) => setRetDriver({ ...retDriver, driver_name: e.target.value })} placeholder="Driver name" className={inputCls} />}
                <div className="flex gap-2">
                  <button data-testid="return-issue-pass-btn" onClick={startReturn} className="flex-1 rounded-xl bg-[#BB162B] py-2.5 text-xs font-bold text-white">Issue Return Gate Pass</button>
                  <button onClick={() => setRet(null)} className="rounded-xl border border-slate-300 px-4 text-xs font-bold text-slate-600">Cancel</button>
                </div>
                <p className="text-[10px] text-slate-500">Security at {t.to_branch_name} scans OUT, Security at {t.from_branch_name} scans IN — the car goes live on the {t.from_branch_name} board automatically.</p>
              </div>
            )}
            {qr === t.id && (t.qr_token || t.return_qr_token) && (
              <div className="mt-3 flex items-center gap-4 rounded-xl bg-[#0E2240] p-4">
                <div className="rounded-lg bg-white p-2"><QRCode value={["returning", "return_in_transit"].includes(t.status) ? t.return_qr_token : t.qr_token} size={90} /></div>
                <div className="text-xs text-slate-300">{["returning", "return_in_transit"].includes(t.status)
                  ? <>Return pass · Security at <b className="text-white">{t.to_branch_name} (OUT)</b> and <b className="text-white">{t.from_branch_name} (IN)</b> with odometer entry.</>
                  : <>Security scans this at the <b className="text-white">sending gate (OUT)</b> and the <b className="text-white">receiving gate (IN)</b> with odometer entry — keeps km reconciliation unbroken.</>}</div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
